/**
 * Embed Config Provider
 * Provides configuration and API URLs to all embed components
 */

import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { AgentOption } from '../components/AgentSelectorDropdown';
import { debugLog } from '../debug';
import { DEFAULT_PRIMARY_COLOR, EmbedThemeConfig } from '../theme';
import type { AdvancedWidgetConfig } from '../theme/types';

// ============================================================================
// Config Types
// ============================================================================

export type AgentIconStyle = 'robot' | 'sparkle' | 'brain' | 'chat' | 'message';

// Re-export AgentOption for convenience
export type { AgentOption };

export interface EmbedConfig {
  // API URLs
  graphqlUrl: string;
  apiBaseUrl: string;
  appBaseUrl: string;
  mediaBaseUrl: string;
  assetApiUrl: string;

  // Theme (basic mode)
  theme: 'light' | 'dark';
  primaryColor: string;

  // Advanced theme configuration
  themeConfig?: EmbedThemeConfig;

  // App settings
  title: string;

  // Owner info (the user whose embed widget this is)
  id?: string;
  ownerHandle?: string;

  // Routing
  path?: string;

  // Callbacks
  onEvent?: (eventName: string, data: unknown) => void;
  /** Callback when editor config changes (for WordPress integration) */
  onConfigChange?: (config: any) => void;

  /** Guest mode - skip login prompts and allow guest interactions */
  guestMode?: boolean;

  /** Initial message to send automatically when the widget loads */
  initialMessage?: string;

  /** Agent icon style for AI messages */
  agentIcon?: AgentIconStyle;

  /** Force showing the login page regardless of authentication state */
  forceLogin?: boolean;

  /** Available agents (brains) for the dropdown selector */
  agents?: AgentOption[];

  /** Currently selected agent */
  selectedAgent?: AgentOption | null;

  /** Callback when agent is changed via dropdown */
  onAgentChange?: (agent: AgentOption) => void;

  /** Show configure button that opens /distribute in a new tab (for external embeds) */
  showConfigureButton?: boolean;

  /** Callback when collapse button is clicked (shows collapse button in header when provided) */
  onCollapse?: () => void;

  /** Advanced widget configuration for fine-grained visual and behavioral customization */
  advancedConfig?: AdvancedWidgetConfig;

  /** Custom conversation starters (pills) — overrides the brain's launch prompts when set */
  conversationStarters?: string[];

  /** Chat bubble style: bubbles (default), minimal, or cards */
  chatStyle?: 'bubbles' | 'minimal' | 'cards';
  /** Header: override the brain's display name */
  displayNameOverride?: string;
  /** Header: subtitle line shown under the name */
  headerSubtitle?: string;
  /** Header: override the brain's display image (URL) */
  displayImageOverride?: string;

  /** Headless mode - hide header/agent selector for embedding within another UI that provides its own */
  headless?: boolean;
}

const defaultConfig: EmbedConfig = {
  graphqlUrl: '',
  apiBaseUrl: '',
  appBaseUrl: '',
  mediaBaseUrl: '',
  assetApiUrl: '',
  theme: 'light',
  primaryColor: DEFAULT_PRIMARY_COLOR,
  title: 'Bolder Space',
  id: undefined,
  guestMode: false,
  agentIcon: 'message',
  agents: [],
  selectedAgent: null,
};

const EmbedConfigContext = createContext<EmbedConfig>(defaultConfig);

export interface EmbedConfigProviderProps {
  config: Partial<EmbedConfig>;
  children: React.ReactNode;
}

export const EmbedConfigProvider: React.FC<EmbedConfigProviderProps> = ({ config, children }) => {
  // Debug logging
  debugLog('[EmbedConfigProvider] ========== RENDER ==========');
  debugLog('[EmbedConfigProvider] Incoming config.forceLogin:', config.forceLogin);
  debugLog('[EmbedConfigProvider] Incoming config.guestMode:', config.guestMode);

  const mergedConfig = useMemo(() => {
    // Derive URLs from graphqlUrl if not provided
    const graphqlUrl = config.graphqlUrl || '';
    const apiBaseUrl = config.apiBaseUrl || graphqlUrl.replace(/\/graphql$/, '');
    const appBaseUrl = config.appBaseUrl || apiBaseUrl.replace('://api.', '://spaces.');
    const mediaBaseUrl = config.mediaBaseUrl || apiBaseUrl.replace('://api.', '://images.') + '/';
    // Asset API URL for WebSocket connection (e.g., https://asset-api.qa.bldr.it)
    const assetApiUrl = config.assetApiUrl || apiBaseUrl.replace('://api.', '://asset-api.');

    const merged = {
      ...defaultConfig,
      ...config,
      graphqlUrl,
      apiBaseUrl,
      appBaseUrl,
      mediaBaseUrl,
      assetApiUrl,
    };
    debugLog('[EmbedConfigProvider] Merged config forceLogin:', merged.forceLogin);
    return merged;
  }, [config]);

  return <EmbedConfigContext.Provider value={mergedConfig}>{children}</EmbedConfigContext.Provider>;
};

export const useEmbedConfig = (): EmbedConfig => {
  return useContext(EmbedConfigContext);
};

export const useEmitEvent = () => {
  const config = useEmbedConfig();
  return useCallback(
    (eventName: string, data: unknown) => {
      config.onEvent?.(eventName, data);
    },
    [config.onEvent],
  );
};
